/**
 * Parse a calendar date string (YYYY-MM-DD) as local midnight.
 * `new Date("2026-04-01")` is interpreted as UTC midnight, which becomes the prior
 * calendar day in US timezones and breaks month boundaries for filters.
 *
 * @param {string|number|Date|null|undefined} input
 * @returns {Date|null}
 */
export function parseLocalDateString(input) {
  if (input == null || input === '') return null;
  if (input instanceof Date) {
    return Number.isNaN(input.getTime()) ? null : input;
  }
  const s = String(input).trim().slice(0, 10);
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (m) {
    const y = Number(m[1]);
    const monthIndex = Number(m[2]) - 1;
    const day = Number(m[3]);
    const d = new Date(y, monthIndex, day);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const d = new Date(input);
  return Number.isNaN(d.getTime()) ? null : d;
}

const DEFAULT_DISPLAY = { month: 'short', day: 'numeric', year: 'numeric' };

/**
 * Format a date-only or ISO string for display using local calendar semantics.
 * @param {object} [options] passed to toLocaleDateString
 */
export function formatLocalDateString(input, options = DEFAULT_DISPLAY, locale = 'en-US') {
  const d = parseLocalDateString(input);
  if (!d) {
    if (input == null || input === '') return '';
    return String(input);
  }
  return d.toLocaleDateString(locale, options);
}

/** YYYY-MM-DD in local timezone (for `<input type="date">` values). */
export function getLocalDateInputValue(date = new Date()) {
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export const DEFAULT_DATE_FILTER_FROM = '2026-01-01';

/** Default date filter: Jan 1 2026 through today (local), not All Time. */
export function getDefaultDateFilter() {
  return {
    includeAllDates: false,
    dateRange: {
      from: DEFAULT_DATE_FILTER_FROM,
      to: getLocalDateInputValue(),
    },
  };
}

/** For sorting: stable local-midnight ms for YYYY-MM-DD (avoids UTC parse). */
export function getTimeFromDateOnly(input) {
  const d = parseLocalDateString(input);
  return d ? d.getTime() : NaN;
}
